/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import {
  useState,
  useEffect,
  useRef,
  TextareaHTMLAttributes,
  forwardRef,
  useImperativeHandle
} from 'react'

type AutoSizeTextareaProps = {
  /** Status of label visibility */
  labelHidden: boolean
} & TextareaHTMLAttributes<HTMLTextAreaElement>

/**
 * TextArea that auto expands based on content.
 * This is unstyled and meant to be used within Textfield
 * Based on https://medium.com/@lucasalgus/creating-a-custom-auto-resize-textarea-component-for-your-react-web-application-6959c0ad68bc
 * */
const AutoSizeTextarea = (
  { labelHidden, onChange, rows, value, ...props }: AutoSizeTextareaProps,
  ref: any
) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [textAreaHeight, setTextAreaHeight] = useState('auto')
  const [parentHeight, setParentHeight] = useState('auto')
  const labelOffset = labelHidden ? 16 : 30

  useImperativeHandle(ref, () => ({
    // this is the PUBLIC API, we are exposing to a consumer via `ref`
    focus: () => {
      textAreaRef.current?.focus()
    }
  }))

  useEffect(() => {
    let height = textAreaRef.current!.scrollHeight - labelOffset

    if (rows) {
      height = Math.max(Number(rows) * 18, height)
    }

    setParentHeight(`${height}px`)
    setTextAreaHeight(`${height}px`)
  }, [value, rows, labelOffset])

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaHeight('auto')
    setParentHeight(`${textAreaRef.current!.scrollHeight - labelOffset}px`)

    if (onChange) {
      onChange(event)
    }
  }

  // outer div prevents scrolling issues
  const parentCss = css`
    min-height: ${parentHeight};

    > textarea {
      padding-top: 20px;
      padding-bottom: 8px;
    }
  `
  const textareaCss = css`
    height: ${textAreaHeight};
    resize: none;
  `

  // always set textarea to one row otherwise the scrollheight will be bigger then expected
  return (
    <div css={parentCss}>
      <textarea
        {...props}
        autoComplete="off"
        css={textareaCss}
        onChange={handleChange}
        ref={textAreaRef}
        rows={1}
        value={value}
      />
    </div>
  )
}

export default forwardRef(AutoSizeTextarea)
